/**
 * Tiny structured logger.
 *
 * Everything is kept in a ring buffer so the Troubleshooting view can
 * show what actually happened on the device, without a backend.
 */

const LEVELS = { debug: 10, info: 20, warn: 30, error: 40 };

const state = {
  level: LEVELS.info,
  buffer: [],
  max: 300,
  sinks: new Set(),
};

export function setLogLevel(name) {
  if (LEVELS[name]) state.level = LEVELS[name];
}

export function onLog(fn) {
  state.sinks.add(fn);
  return () => state.sinks.delete(fn);
}

export function getLogEntries() {
  return state.buffer.slice();
}

function write(level, scope, message, detail) {
  if (LEVELS[level] < state.level) return;
  const entry = {
    time: Date.now(),
    level,
    scope,
    message,
    detail: detail === undefined ? undefined : safeDetail(detail),
  };
  state.buffer.push(entry);
  if (state.buffer.length > state.max) state.buffer.shift();
  for (const sink of state.sinks) {
    try {
      sink(entry);
    } catch {
      /* a broken sink must never break logging */
    }
  }
  const line = `[${scope}] ${message}`;
  if (level === 'error') console.error(line, detail ?? '');
  else if (level === 'warn') console.warn(line, detail ?? '');
  else if (level === 'debug') console.debug(line, detail ?? '');
  else console.log(line, detail ?? '');
}

function safeDetail(detail) {
  if (detail instanceof Error) {
    return { name: detail.name, message: detail.message, stack: detail.stack?.split('\n').slice(0, 4).join(' | ') };
  }
  if (typeof detail === 'object' && detail !== null) {
    try {
      JSON.stringify(detail);
      return detail;
    } catch {
      return String(detail);
    }
  }
  return detail;
}

export function createLogger(scope) {
  return {
    debug: (message, detail) => write('debug', scope, message, detail),
    info: (message, detail) => write('info', scope, message, detail),
    warn: (message, detail) => write('warn', scope, message, detail),
    error: (message, detail) => write('error', scope, message, detail),
  };
}

export const log = createLogger('app');
